import { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Search, Check, ChevronRight, Building2 } from 'lucide-react';
import Modal from './Modal';
import { selectCurrentUser } from '../../redux/user/user.selectors';
import { selectCurrentDistro } from '../../redux/distro/distro.selector';
import { setCurrentDistro } from '../../redux/distro/distro.actions';

const AccountSwitcherModal = ({
    isOpen,
    onClose,
    currentUser,
    currentDistro,
    setCurrentDistro,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const distros = currentUser?.distros || [];
    const showSearch = distros.length > 5;

    const filteredDistros = useMemo(() => {
        if (!searchQuery.trim()) return distros;
        const query = searchQuery.toLowerCase();
        return distros.filter(distro =>
            distro.name.toLowerCase().includes(query)
        );
    }, [distros, searchQuery]);

    const handleSelect = (distro) => {
        setCurrentDistro(distro.id);
        setSearchQuery('');
        onClose();
    };

    const handleClose = () => {
        setSearchQuery('');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Switch Firm"
            size="sm"
        >
            <div className="space-y-4">
                {/* account count */}
                <p className="text-sm text-gray-500">
                    You have access to {distros.length} firm{distros.length !== 1 ? 's' : ''}
                </p>

                {/* search input - show if more than 5 distros */}
                {showSearch && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search firms..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all placeholder:text-gray-400"
                            autoFocus
                        />
                    </div>
                )}

                {/* distro list */}
                <div className="space-y-2 max-h-80 overflow-y-auto">
                    {filteredDistros.length > 0 ? (
                        filteredDistros.map((distro) => {
                            const isActive = currentDistro === distro.id;
                            return (
                                <button
                                    key={distro.id}
                                    onClick={() => handleSelect(distro)}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${
                                        isActive
                                            ? 'bg-primary/10 border border-primary/30'
                                            : 'bg-gray-50 border border-gray-200 hover:bg-primary/5 hover:border-primary/30'
                                    }`}
                                >
                                    <div className={`p-2 rounded-lg ${isActive ? 'bg-primary/20' : 'bg-white'}`}>
                                        <Building2 className={`w-4 h-4 ${isActive ? 'text-primary' : 'text-gray-400'}`} />
                                    </div>
                                    <span className={`flex-1 text-left text-sm font-medium ${
                                        isActive ? 'text-primary' : 'text-gray-900'
                                    }`}>
                                        {distro.name}
                                    </span>
                                    {isActive ? (
                                        <Check className="w-4 h-4 text-primary" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-400" />
                                    )}
                                </button>
                            );
                        })
                    ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                            No firms match your search
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
    currentDistro: selectCurrentDistro,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentDistro: (distro) => dispatch(setCurrentDistro(distro)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountSwitcherModal);
